/**
 * KENAGRI Lightweight i18n System
 *
 * No external dependencies. Supports dot-notation keys and parameter interpolation.
 */

export type Language = 'en' | 'sw';
type TranslationKey = string;

const LANGUAGE_STORAGE_KEY = 'kenagri_language';
const DEFAULT_LANGUAGE: Language = 'en';

let currentLanguage: Language = DEFAULT_LANGUAGE;
let translations: Record<string, string> = {};

// Flatten a nested object into dot-notation keys
// e.g. { nav: { dashboard: "Dashboard" } } => { "nav.dashboard": "Dashboard" }
function flattenObject(
  obj: Record<string, unknown>,
  prefix = ''
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];

    if (typeof value === 'string') {
      result[fullKey] = value;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, fullKey));
    }
  }

  return result;
}

/**
 * Load translations for a language and flatten them into dot-notation.
 */
export function loadTranslations(_lang: Language): Record<string, string> {
  // Dynamic import is handled by the LanguageContext.
  // This function processes raw JSON into a flat map.
  return {};
}

/**
 * Set the flattened translations map directly (called by LanguageContext after loading JSON).
 */
export function setTranslations(flat: Record<string, string>): void {
  translations = flat;
}

/**
 * Process raw locale JSON into a flat translations map.
 */
export function processLocaleData(data: Record<string, unknown>): Record<string, string> {
  return flattenObject(data);
}

/**
 * Translate a key with optional parameter interpolation.
 *
 * Supports:
 *   - Dot notation: t('nav.dashboard')
 *   - Parameter interpolation: t('common.lastDays', { days: 30 }) => "Last 30 days"
 *     Parameters use {{param}} syntax in translation strings.
 */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  let value = translations[key];

  if (value === undefined) {
    // Return the key itself as a fallback so missing translations are visible
    return key;
  }

  if (params) {
    for (const [paramKey, paramValue] of Object.entries(params)) {
      value = value.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), String(paramValue));
    }
  }

  return value;
}

/**
 * Get the current language.
 */
export function getCurrentLanguage(): Language {
  return currentLanguage;
}

/**
 * Set the current language. Persists to localStorage.
 */
export function setLanguage(lang: Language): void {
  currentLanguage = lang;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // localStorage may not be available (SSR, privacy mode)
  }
}

/**
 * Initialize language from localStorage (call once on app start).
 */
export function initLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'sw') {
      currentLanguage = stored;
    }
  } catch {
    // Fallback to default
  }
  return currentLanguage;
}

/**
 * Get all available languages with display names.
 */
export function getAvailableLanguages(): Array<{ code: Language; name: string; nativeName: string }> {
  return [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili' },
  ];
}
